// Curated Project Risk Register: an editorial overlay over each project's
// active source risks. Source risks are never mutated here; the register
// stores selections, display-text overrides, ad-hoc items and a project level.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { ProjectsTableScroll } from "../ProjectsScrollLayout.jsx";
import { getRiskRegister, saveRiskRegister } from "../../../data/projectRiskRegisterClient.js";
import { fmtDate } from "../../../utils/format.js";
import { countLevels, levelMeta, printRiskRegister, RISK_LEVEL_META } from "./riskPrint.js";

export const RISK_WRITE_ROLES = new Set(["admin"]);

const s = {
  btn: { border: `1px solid ${T.border}`, background: T.card, borderRadius: 9, padding: "7px 12px", fontSize: T.fsSm5, fontWeight: T.fwBold, color: T.projectsHubNavy, cursor: "pointer" },
  primary: { background: T.projectsHubNavy, color: T.white, borderColor: T.projectsHubNavy },
  link: { background: "none", border: "none", padding: 0, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, cursor: "pointer" },
  err: { color: T.red, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: "8px 10px", fontSize: T.fsXs, marginTop: 8 },
  th: { fontSize: T.fsXxs, letterSpacing: ".04em", textTransform: "uppercase", color: T.mid, textAlign: "left", padding: "11px 16px", borderBottom: `2px solid ${T.borderDark}`, fontWeight: T.fwHubTitle, background: T.hoverBg },
  td: { padding: "14px 16px", borderBottom: `1px solid ${T.lightGray}`, verticalAlign: "top" },
  input: { width: "100%", border: `1px solid ${T.borderDark}`, borderRadius: 7, padding: "6px 8px", fontSize: T.fsSm, color: T.dark, background: T.card, boxSizing: "border-box" },
  sec: { fontSize: T.fsXxs5, fontWeight: T.fwBold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid, margin: "12px 0 6px" },
};

export const itemText = (it) => (it?.overrideText != null && it.overrideText !== "" ? it.overrideText : it?.text ?? "");

export function provenanceText(row) {
  if (!row || !row.updatedAt) return "Never updated";
  return `Last updated by ${row.updatedBy || "unknown editor"} \u00b7 ${fmtDate(row.updatedAt)}`;
}

export function latestProvenance(rows) {
  let best = null;
  for (const r of rows) if (r?.updatedAt && (!best || String(r.updatedAt) > String(best.updatedAt))) best = r;
  return best ? provenanceText(best) : null;
}

export function draftFromRow(row) {
  const sources = {};
  for (const c of row.candidates || []) if (c.selected) sources[c.id] = c.overrideText ?? null;
  return {
    revision: row.revision ?? 0,
    level: row.level ?? null,
    sources,
    adhoc: (row.items || []).filter((i) => i.sourceRiskId == null).map((i) => i.text ?? ""),
  };
}

export function payloadFromDraft(draft, candidates) {
  // Keep draft selections even if a focus refresh loses an active candidate.
  // The server must reject that stale selection visibly, not silently drop it.
  const order = [...new Set([...(candidates || []).map((c) => c.id), ...Object.keys(draft.sources)])];
  return {
    revision: draft.revision,
    level: draft.level,
    sources: order.filter((id) => id in draft.sources).map((id) => {
      const o = draft.sources[id];
      return { sourceRiskId: id, overrideText: o != null && o.trim() !== "" ? o.trim() : null };
    }),
    adhoc: draft.adhoc.map((t) => t.trim()),
  };
}

const errMsg = (e) => e?.message || String(e || "Request failed.");
const isConflict = (e) => e?.status === 409 || e?.code === "revision_conflict" || e?.code === "REVISION_CONFLICT";

function LevelChip({ level, testId }) {
  const m = levelMeta(level);
  return <span data-testid={testId} style={{ display: "inline-block", border: `1px solid ${m.border}`, background: m.bg, color: m.fg, borderRadius: 7, padding: "2px 10px", fontSize: T.fsXs, fontWeight: T.fwBold, whiteSpace: "nowrap" }}>{m.label}</span>;
}

function SummaryPill({ label, count, color, testId }) {
  return (
    <span data-testid={testId} style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${T.border}`, background: T.card, borderRadius: 999, padding: "5px 11px", fontSize: T.fsSm, fontWeight: T.fwBold, color: T.dark }}>
      <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
      {label} {count}
    </span>
  );
}

function SkeletonRows({ n }) {
  return Array.from({ length: n }).map((_, i) => (
    <tr key={i} data-testid="risk-skeleton">
      {[40, 14, 70].map((w, j) => <td key={j} style={s.td}><div style={{ height: 12, width: `${w}%`, background: T.lightGray, borderRadius: 4 }} /></td>)}
    </tr>
  ));
}

function RegisterEditor({ row, draft, setDraft, saving, error, conflict, onSave, onCancel, onReload }) {
  const pid = row.projectId;
  const toggle = (c) => setDraft((d) => {
    const next = { ...d.sources };
    if (c.id in next) delete next[c.id];
    else if (c.active) next[c.id] = null;
    return { ...d, sources: next };
  });
  const setOverride = (id, v) => setDraft((d) => ({ ...d, sources: { ...d.sources, [id]: v } }));
  const setAdhoc = (fn) => setDraft((d) => ({ ...d, adhoc: fn(d.adhoc) }));
  const move = (i, dir) => setAdhoc((a) => {
    const j = i + dir; if (j < 0 || j >= a.length) return a;
    const n = [...a]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const cands = row.candidates || [];
  const shown = cands.filter((c) => c.active || c.id in draft.sources);
  return (
    <div data-testid={`risk-editor-${pid}`} style={{ background: T.blueSoft, border: `1px solid ${T.blueSoftBorder}`, borderRadius: 10, padding: 12, marginTop: 10 }}>
      <div style={{ ...s.sec, marginTop: 0 }}>Risk level</div>
      <div role="radiogroup" aria-label="Risk level" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[...Object.keys(RISK_LEVEL_META), null].map((lv) => {
          const m = levelMeta(lv); const on = draft.level === lv;
          return (
            <button key={lv ?? "none"} type="button" role="radio" aria-checked={on} data-testid={`risk-level-${pid}-${lv ?? "none"}`} disabled={saving}
              onClick={() => setDraft((d) => ({ ...d, level: lv }))}
              style={{ ...s.btn, padding: "4px 11px", background: on ? m.bg : T.card, color: on ? m.fg : T.mid, borderColor: on ? m.fg : T.border }}>{m.label}</button>
          );
        })}
      </div>

      <div style={s.sec}>Source risks</div>
      {shown.length === 0 && <div style={{ color: T.mid, fontSize: T.fsSm, fontStyle: "italic" }}>This project has no active source risks.</div>}
      {shown.map((c) => {
        const sel = c.id in draft.sources;
        const ov = draft.sources[c.id];
        return (
          <div key={c.id} data-testid={`risk-candidate-${c.id}`} style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px", marginBottom: 6 }}>
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: T.fsSm, color: c.active ? T.dark : T.mid, cursor: "pointer" }}>
              <input type="checkbox" checked={sel} disabled={saving || (!sel && !c.active)} onChange={() => toggle(c)} data-testid={`risk-candidate-toggle-${c.id}`} />
              <span style={{ overflowWrap: "anywhere" }}>{c.title}{c.status ? <span style={{ color: T.mid, fontSize: T.fsXxs5 }}> ({c.status})</span> : null}</span>
            </label>
            {!c.active && (
              <div data-testid={`risk-candidate-inactive-${c.id}`} style={{ fontSize: T.fsXs, color: T.ragAmberText, background: T.ragAmberBg, border: `1px solid ${T.ragAmberBorder}`, borderRadius: 6, padding: "4px 8px", marginTop: 6 }}>
                This source risk is no longer active, so it is excluded from the register. Deselect it to clean up; it cannot be re-included.
              </div>
            )}
            {sel && c.active && (
              <div style={{ marginTop: 6, paddingLeft: 22 }}>
                {ov == null ? (
                  <button type="button" style={s.link} disabled={saving} onClick={() => setOverride(c.id, c.title || "")} data-testid={`risk-override-start-${c.id}`}>Reword for register</button>
                ) : (
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <input aria-label={`Display text for ${c.title}`} value={ov} disabled={saving} onChange={(e) => setOverride(c.id, e.target.value)} style={s.input} data-testid={`risk-override-input-${c.id}`} />
                    <button type="button" style={{ ...s.link, whiteSpace: "nowrap" }} disabled={saving} onClick={() => setOverride(c.id, null)} data-testid={`risk-override-clear-${c.id}`}>Use source wording</button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      <div style={s.sec}>Register-only items</div>
      {draft.adhoc.map((t, i) => (
        <div key={i} style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
          <input aria-label={`Register-only item ${i + 1}`} value={t} disabled={saving} onChange={(e) => setAdhoc((a) => a.map((x, k) => (k === i ? e.target.value : x)))} style={s.input} data-testid={`risk-adhoc-input-${pid}-${i}`} />
          <button type="button" style={s.link} disabled={saving || i === 0} onClick={() => move(i, -1)} aria-label={`Move item ${i + 1} up`} data-testid={`risk-adhoc-up-${pid}-${i}`}>Up</button>
          <button type="button" style={s.link} disabled={saving || i === draft.adhoc.length - 1} onClick={() => move(i, 1)} aria-label={`Move item ${i + 1} down`} data-testid={`risk-adhoc-down-${pid}-${i}`}>Down</button>
          <button type="button" style={{ ...s.link, color: T.red }} disabled={saving} onClick={() => setAdhoc((a) => a.filter((_, k) => k !== i))} aria-label={`Remove item ${i + 1}`} data-testid={`risk-adhoc-remove-${pid}-${i}`}>Remove</button>
        </div>
      ))}
      <button type="button" style={s.link} disabled={saving} onClick={() => setAdhoc((a) => [...a, ""])} data-testid={`risk-adhoc-add-${pid}`}>+ Add register-only item</button>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button type="button" style={{ ...s.btn, ...s.primary }} disabled={saving || conflict} onClick={onSave} data-testid={`risk-save-${pid}`}>{saving ? "Saving..." : "Save register"}</button>
        <button type="button" style={s.btn} disabled={saving} onClick={onCancel} data-testid={`risk-cancel-${pid}`}>Cancel</button>
      </div>
      {conflict && (
        <div role="alert" data-testid={`risk-conflict-${pid}`} style={s.err}>
          Someone else saved this register since you started editing. {error}
          <div style={{ marginTop: 6 }}><button type="button" style={{ ...s.link, color: T.red }} onClick={onReload} data-testid={`risk-conflict-reload-${pid}`}>Reload latest (discards this draft)</button></div>
        </div>
      )}
      {!conflict && error && <div role="alert" data-testid={`risk-save-error-${pid}`} style={s.err}>{error}</div>}
    </div>
  );
}

export function ProjectRiskRegister({ projects, currentRole }) {
  const list = useMemo(() => (Array.isArray(projects) ? projects : []), [projects]);
  const ids = useMemo(() => list.map((p) => p.id), [list]);
  const idsKey = ids.join("|");
  const canEdit = RISK_WRITE_ROLES.has(currentRole);

  const [load, setLoad] = useState({ status: "loading", rows: {}, error: null });
  const [edit, setEdit] = useState(null); // { projectId, draft, saving, error, conflict }
  const [printError, setPrintError] = useState(null);
  const reqRef = useRef(0);
  const abortRef = useRef(null);

  const fetchRows = useCallback((silent) => {
    abortRef.current?.abort();
    const ctrl = typeof AbortController !== "undefined" ? new AbortController() : null;
    abortRef.current = ctrl;
    const my = ++reqRef.current;
    if (!silent) setLoad((l) => ({ ...l, status: "loading", error: null }));
    const projectIds = idsKey ? idsKey.split("|") : [];
    if (projectIds.length === 0) { setLoad({ status: "ok", rows: {}, error: null }); return; }
    Promise.resolve()
      .then(() => getRiskRegister(projectIds, ctrl ? { signal: ctrl.signal } : undefined))
      .then((res) => {
        if (my !== reqRef.current) return;
        const rows = {};
        for (const r of res?.rows || []) rows[r.projectId] = r;
        setLoad({ status: "ok", rows, error: null });
      })
      .catch((e) => {
        if (my !== reqRef.current || e?.name === "AbortError") return;
        // A failed silent refresh keeps the last good data visible.
        setLoad((l) => (silent && l.status === "ok" ? l : { status: "error", rows: {}, error: e }));
      });
  }, [idsKey]);

  useEffect(() => { fetchRows(false); return () => abortRef.current?.abort(); }, [fetchRows]);

  useEffect(() => {
    const onFocus = () => fetchRows(true);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [fetchRows]);

  // Role loss: drop the editor entirely.
  useEffect(() => { if (!canEdit) setEdit(null); }, [canEdit]);

  const displayRows = useMemo(() => list.map((p) => {
    const row = load.rows[p.id];
    return {
      project: p,
      row,
      name: p.name,
      location: [p.locationAlias || p.locationName, p.locationRegion].filter(Boolean).join(" \u00b7 "),
      level: row?.level ?? null,
      items: (row?.items || []).map(itemText),
      provenance: provenanceText(row),
    };
  }), [list, load.rows]);

  const counts = countLevels(displayRows);
  const latest = latestProvenance(displayRows.map((d) => d.row).filter(Boolean));
  const ready = load.status === "ok";

  const startEdit = (row) => setEdit({ projectId: row.projectId, draft: draftFromRow(row), saving: false, error: null, conflict: false });
  const setDraft = (fn) => setEdit((e) => (e ? { ...e, draft: fn(e.draft), error: e.conflict ? e.error : null } : e));

  const save = async () => {
    if (!edit || !canEdit) return;
    abortRef.current?.abort();
    ++reqRef.current;
    const row = load.rows[edit.projectId];
    if (edit.draft.adhoc.some((t) => !t.trim())) { setEdit((e) => ({ ...e, error: "Register-only items cannot be blank. Fill them in or remove them." })); return; }
    const pid = edit.projectId;
    setEdit((e) => ({ ...e, saving: true, error: null }));
    try {
      const saved = await saveRiskRegister(pid, payloadFromDraft(edit.draft, row?.candidates));
      setLoad((l) => ({ ...l, rows: { ...l.rows, [pid]: saved } }));
      setEdit((e) => (e && e.projectId === pid ? null : e));
    } catch (err) {
      setEdit((e) => (e && e.projectId === pid ? { ...e, saving: false, error: errMsg(err), conflict: isConflict(err) } : e));
    }
  };

  const reloadAfterConflict = () => { setEdit(null); fetchRows(true); };

  const onPrint = () => {
    const err = printRiskRegister({
      rows: displayRows.map(({ name, location, level, items, provenance }) => ({ name, location, level, items, provenance })),
      latest,
      generatedAt: fmtDate(new Date().toISOString()),
    });
    setPrintError(err);
  };

  return (
    <div data-testid="risk-register" style={{ color: T.dark }}>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <SummaryPill label="High" count={ready ? counts.high : "-"} color={T.ragRed} testId="risk-count-high" />
            <SummaryPill label="Med" count={ready ? counts.med : "-"} color={T.ragAmber} testId="risk-count-med" />
            <SummaryPill label="Low" count={ready ? counts.low : "-"} color={T.greenDeep} testId="risk-count-low" />
          </div>
          <div data-testid="risk-latest-provenance" style={{ fontSize: T.fsXs, color: T.mid, marginTop: 6 }}>
            {ready ? (latest || "No register edits yet") : "\u00a0"}
          </div>
        </div>
        <button type="button" style={s.btn} onClick={onPrint} disabled={!ready} data-testid="risk-export-pdf">Export PDF</button>
      </div>
      {printError && <div role="alert" data-testid="risk-print-error" style={{ ...s.err, marginTop: 0, marginBottom: 10 }}>{printError}</div>}

      {load.status === "error" && (
        <div role="alert" data-testid="risk-load-error" style={{ ...s.err, marginTop: 0, marginBottom: 10 }}>
          Could not load the risk register: {errMsg(load.error)}
          <button type="button" style={{ ...s.link, color: T.red, marginLeft: 8 }} onClick={() => fetchRows(false)} data-testid="risk-retry">Retry</button>
        </div>
      )}

      <ProjectsTableScroll testId="projects-risks-scroll" style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: T.fsBase }}>
          <thead><tr><th style={{ ...s.th, width: "26%" }}>Project</th><th style={{ ...s.th, width: "12%" }}>Risk</th><th style={s.th}>Details</th></tr></thead>
          <tbody>
            {load.status === "loading" && <SkeletonRows n={Math.min(Math.max(list.length, 1), 5)} />}
            {ready && displayRows.length === 0 && <tr><td colSpan={3} style={{ ...s.td, color: T.mid, fontStyle: "italic", textAlign: "center", padding: 28 }}>No active projects to show.</td></tr>}
            {ready && displayRows.map((d) => {
              const pid = d.project.id;
              const editing = canEdit && edit?.projectId === pid && d.row;
              return (
                <tr key={pid} data-testid={`risk-row-${pid}`}>
                  <td style={s.td}>
                    <div style={{ fontWeight: T.fwBold, color: T.projectsHubNavy, overflowWrap: "anywhere" }}>{d.name}</div>
                    {d.location && <div style={{ fontSize: T.fsXs, color: T.mid }}>{d.location}</div>}
                  </td>
                  <td style={s.td}><LevelChip level={d.level} testId={`risk-level-chip-${pid}`} /></td>
                  <td style={s.td}>
                    {!d.row && <div style={{ color: T.mid, fontSize: T.fsSm }} data-testid={`risk-missing-${pid}`}>Register data unavailable for this project.</div>}
                    {d.row && (d.items.length === 0
                      ? <div data-testid={`risk-empty-${pid}`} style={{ color: T.mid, fontStyle: "italic" }}>No risks to report</div>
                      : <ul data-testid={`risk-items-${pid}`} style={{ margin: 0, paddingLeft: 18 }}>{d.items.map((t, i) => <li key={i} style={{ margin: "2px 0", overflowWrap: "anywhere" }}>{t}</li>)}</ul>)}
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 6, alignItems: "center" }}>
                      <span data-testid={`risk-provenance-${pid}`} style={{ fontSize: T.fsXxs5, color: T.light }}>{d.provenance}</span>
                      {canEdit && d.row && !editing && <button type="button" style={s.link} disabled={!!edit} onClick={() => startEdit(d.row)} data-testid={`risk-edit-${pid}`} aria-label={`Edit register for ${d.name}`}>Edit register</button>}
                    </div>
                    {editing && (
                      <RegisterEditor row={d.row} draft={edit.draft} setDraft={setDraft} saving={edit.saving} error={edit.error} conflict={edit.conflict}
                        onSave={save} onCancel={() => setEdit(null)} onReload={reloadAfterConflict} />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </ProjectsTableScroll>
    </div>
  );
}

export default ProjectRiskRegister;
